// @file vuecomposable for managing map format and functionality

import device from '@@/bits/device'
import { OverlappingMarkerSpiderfier, type GeoMap, type PostMarkerObject } from '@@/bits/map'
import { GoogleMapManager } from '@@/bits/map_format/GoogleMapManager'
import type { MapManager } from '@@/bits/map_format/MapManager'
import { getMarkerColorsFromThemeId } from '@@/bits/map_theme'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import type { ComputedRef, Ref } from 'vue'
import { computed, nextTick, ref, watch } from 'vue'

interface UseMapOptions {
  rootEl: Ref<HTMLElement | null>
  mapRenderer: 'google' | 'leaflet'
  latitude: ComputedRef<number>
  longitude: ComputedRef<number>
  zoom: ComputedRef<number>
  themeId: ComputedRef<number>
  backgroundColor: ComputedRef<string>
  markers: ComputedRef<PostMarkerObject[]>
  isAPopupOpen: ComputedRef<boolean>
  onMapClick?: (e: Event & { placeId?: string; stop: () => void }) => void
  onMapInitialized?: (map: GeoMap) => void
}

/**
 * Composable for managing map format and functionality.
 * Handles map initialization, theme management, markers, and map interactions.
 *
 * @param options - Configuration options for the map
 * @param options.rootEl - Reference to the root HTML element where map will be mounted
 * @param options.mapRenderer - Map provider to use ('google' or 'leaflet')
 * @param options.latitude - Initial latitude position (as computed ref)
 * @param options.longitude - Initial longitude position (as computed ref)
 * @param options.zoom - Initial zoom level (as computed ref)
 * @param options.themeId - Theme ID for map styling (as computed ref)
 * @param options.backgroundColor - Background color of the map (as computed ref)
 * @param options.markers - Array of marker objects to display on the map (as computed ref)
 * @param options.isAPopupOpen - Computed ref tracking if a popup is open
 * @param options.onMapClick - Optional click handler for the map
 * @param options.onMapInitialized - Optional callback when map is initialized
 * @returns Object containing map manager, initialization function and map controls
 */
export function useSurfaceMapFormat({
  rootEl,
  mapRenderer,
  latitude,
  longitude,
  zoom,
  themeId,
  backgroundColor,
  markers,
  isAPopupOpen,
  onMapClick,
  onMapInitialized,
}: UseMapOptions): {
  mapManager: typeof mapManager
  initializeMap: typeof initializeMap
  map: typeof map
  markerIcons: typeof markerIcons
  zoomIn: typeof zoomIn
  zoomOut: typeof zoomOut
  fitBounds: typeof fitBounds
} {
  const surfaceMapStore = useSurfaceMapStore()
  let mapManager: MapManager | null = null
  const map = ref<GeoMap | null>(null)
  const markerIcons = computed(() => {
    const icons = mapManager?.getMarkerIcons()
    return icons ?? getMarkerColorsFromThemeId(themeId.value)
  })

  const initializeMap = (): void => {
    if (rootEl.value == null) return

    if (mapRenderer === 'google') {
      mapManager = new GoogleMapManager(rootEl.value, {
        zoomControl: false,
        minZoom: device.mobile ? undefined : 2,
        backgroundColor: backgroundColor.value,
        cameraControl: false,
      })
    }

    mapManager?.initialize(latitude.value, longitude.value, zoom.value)
    mapManager?.setTheme(themeId.value)
    mapManager?.setPopupMode(isAPopupOpen.value)

    const mapInstance = mapManager?.getMap()
    if (mapInstance == null) return

    map.value = mapInstance

    // Setup click listener
    if (onMapClick != null) {
      mapManager?.addClickListener((e: Event) => {
        // Cast the event to include the expected properties
        const mapEvent = e as Event & { placeId?: string; stop: () => void }
        onMapClick(mapEvent)
      })
    }

    // Setup spiderfier after map is initialized
    if (mapRenderer === 'google') {
      const spiderfier = new OverlappingMarkerSpiderfier(mapInstance, {
        circleFootSeparation: 30,
        keepSpiderfied: true,
        nearbyDistance: 1,
      })
      surfaceMapStore.setMarkerOverlayLayer({
        spiderfier,
      })
    }

    onMapInitialized?.(mapInstance)
  }

  // Watch for theme changes
  watch(themeId, (newThemeId) => {
    mapManager?.setTheme(newThemeId)
  })

  // Watch for popup mode changes
  watch(
    isAPopupOpen,
    (isOpen) => {
      mapManager?.setPopupMode(isOpen)
    },
    { immediate: true },
  )

  // Watch for marker changes and handle map fitting
  watch(
    markers,
    () => {
      if (mapManager?.isMapFittedBoundsOnLoad() !== true) {
        // Fit the map immediately after the map receives markers at the first time
        nextTick(() => {
          mapManager?.fitBoundsForMarkers(markers.value, {
            lat: latitude.value,
            lng: longitude.value,
            zoom: zoom.value,
          })
          mapManager?.setMapFittedBoundsOnLoad(true)
        })
      } else if (surfaceMapStore.mapDoesFitMarkersBounds) {
        // Fit the map on markers changed only if we allow the map to fit
        // Example of some cases where we do not want it to fit:
        // - User is adding a post
        // - User is editing a post
        mapManager?.fitBoundsForMarkers(markers.value, { lat: latitude.value, lng: longitude.value, zoom: zoom.value })
      }
    },
    { immediate: true },
  )

  // Watch for map center and zoom changes
  watch([latitude, longitude, zoom], ([newLat, newLng, newZoom]) => {
    if (markers.value.length === 0 && mapManager != null) {
      mapManager?.setCenter({ lat: newLat, lng: newLng })
      mapManager?.setZoom(newZoom)
    }
  })

  const zoomIn = (): void => {
    mapManager?.zoomIn()
  }

  const zoomOut = (): void => {
    mapManager?.zoomOut()
  }

  const fitBounds = (): void => {
    mapManager?.fitBoundsForMarkers(markers.value, { lat: latitude.value, lng: longitude.value, zoom: zoom.value })
  }

  return {
    mapManager,
    map,
    markerIcons,
    initializeMap,
    zoomIn,
    zoomOut,
    fitBounds,
  }
}
